import { useCallback, useEffect, useMemo, useState } from 'react';
import { toScheduledFromIsoFromPickerDate } from '../utils/toScheduledFromIsoFromPickerDate';
import { toScheduledToIsoFromPickerDate } from '../utils/toScheduledToIsoFromPickerDate';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/hooks';
import { RoutesFilter } from '../models';
import { RouteStatus } from '../enums';
import { pickDriverLabelsForIds } from '../utils/driverAutocomplete';

const ROUTES_FILTERS_STORAGE_KEY = 'BUSINESS-MONITORING-ROUTES-FILTERS';

const normalizeDriverIdsFromUnknown = (
  value: unknown
): string[] | undefined => {
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    const ids = [
      ...new Set(value.map((s) => String(s).trim()).filter(Boolean)),
    ];
    return ids.length > 0 ? ids : undefined;
  }
  if (typeof value === 'string' && value.trim()) {
    const ids = [
      ...new Set(value.split(',').map((s) => s.trim()).filter(Boolean)),
    ];
    return ids.length > 0 ? ids : undefined;
  }
  return undefined;
};

const driverIdsFromCommaQuery = (raw: string | null): string[] | undefined => {
  if (!raw?.trim()) return undefined;
  const ids = [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))];
  return ids.length > 0 ? ids : undefined;
};

const normalizeDriverLabelsFromUnknown = (
  value: unknown
): Record<string, string> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val === 'string' && val.trim().length > 0) {
      out[key] = val.trim();
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const ROUTES_FILTER_QUERY_FIELDS = {
  string: [
    'logisticOperatorId',
    'commerceId',
    'scheduledFrom',
    'scheduledTo',
    'routeId',
    'routeName',
    'shippingNumber',
    'status',
  ],
} satisfies {
  string: readonly (keyof RoutesFilter)[];
};

const resolveInitialRoutesFilter = (
  emptyFilterTemplate: RoutesFilter,
  locationSearch: string,
  readQueryParam: (key: string) => string | null
): RoutesFilter => {
  const storageJson = localStorage.getItem(ROUTES_FILTERS_STORAGE_KEY);
  let filterAfterStorage: RoutesFilter = emptyFilterTemplate;

  if (storageJson) {
    try {
      const parsed = JSON.parse(storageJson) as Record<string, unknown>;

      filterAfterStorage = {
        ...emptyFilterTemplate,
        driverIds:
          normalizeDriverIdsFromUnknown(parsed.driverIds ?? parsed.driverId) ??
          emptyFilterTemplate.driverIds,
        driverLabelsById:
          normalizeDriverLabelsFromUnknown(parsed.driverLabelsById) ??
          emptyFilterTemplate.driverLabelsById,
        logisticOperatorId:
          typeof parsed.logisticOperatorId === 'string'
            ? parsed.logisticOperatorId
            : emptyFilterTemplate.logisticOperatorId,
        commerceId:
          typeof parsed.commerceId === 'string'
            ? parsed.commerceId
            : emptyFilterTemplate.commerceId,
        scheduledFrom:
          typeof parsed.scheduledFrom === 'string'
            ? parsed.scheduledFrom
            : emptyFilterTemplate.scheduledFrom,
        scheduledTo:
          typeof parsed.scheduledTo === 'string'
            ? parsed.scheduledTo
            : emptyFilterTemplate.scheduledTo,
        routeId:
          typeof parsed.routeId === 'string'
            ? parsed.routeId
            : emptyFilterTemplate.routeId,
        routeName:
          typeof parsed.routeName === 'string'
            ? parsed.routeName
            : emptyFilterTemplate.routeName,
        shippingNumber:
          typeof parsed.shippingNumber === 'string'
            ? parsed.shippingNumber
            : emptyFilterTemplate.shippingNumber,
        status:
          typeof parsed.status === 'string' &&
          parsed.status &&
          Object.values(RouteStatus).includes(parsed.status as RouteStatus)
            ? (parsed.status as RouteStatus)
            : emptyFilterTemplate.status,
      };
    } catch {
      filterAfterStorage = emptyFilterTemplate;
    }
  }

  const urlParams = new URLSearchParams(locationSearch);
  const filterAfterUrl = { ...filterAfterStorage };

  for (const key of ROUTES_FILTER_QUERY_FIELDS.string) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    (filterAfterUrl as Record<string, unknown>)[key] = fromUrl ?? '';
  }

  if (urlParams.has('driverIds')) {
    filterAfterUrl.driverIds = driverIdsFromCommaQuery(
      readQueryParam('driverIds')
    );
  } else if (urlParams.has('driverId')) {
    const legacy = readQueryParam('driverId')?.trim();
    filterAfterUrl.driverIds = legacy ? [legacy] : undefined;
  }

  const driverIdsResolved = filterAfterUrl.driverIds;
  if (driverIdsResolved?.length && filterAfterUrl.driverLabelsById) {
    filterAfterUrl.driverLabelsById = pickDriverLabelsForIds(
      driverIdsResolved,
      filterAfterUrl.driverLabelsById
    );
  } else if (!driverIdsResolved?.length) {
    filterAfterUrl.driverLabelsById = undefined;
  }

  const statusAfterUrl = filterAfterUrl.status;
  if (typeof statusAfterUrl === 'string') {
    filterAfterUrl.status =
      statusAfterUrl &&
      Object.values(RouteStatus).includes(statusAfterUrl as RouteStatus)
        ? (statusAfterUrl as RouteStatus)
        : undefined;
  }

  return filterAfterUrl;
};

const saveRoutesFilterToLocalStorage = (filter: RoutesFilter) => {
  const jsonSafeFilter = {
    driverIds: filter.driverIds,
    driverLabelsById: filter.driverLabelsById,
    logisticOperatorId: filter.logisticOperatorId,
    commerceId: filter.commerceId,
    scheduledFrom: filter.scheduledFrom,
    scheduledTo: filter.scheduledTo,
    routeId: filter.routeId,
    routeName: filter.routeName,
    shippingNumber: filter.shippingNumber,
    status: filter.status,
  };
  try {
    localStorage.setItem(
      ROUTES_FILTERS_STORAGE_KEY,
      JSON.stringify(jsonSafeFilter)
    );
  } catch {

  }
};

const buildUrlQueryFromRoutesFilter = (filter: RoutesFilter) => ({
  driverId: undefined,
  driverIds: filter.driverIds?.length ? filter.driverIds.join(',') : undefined,
  logisticOperatorId: filter.logisticOperatorId,
  commerceId: filter.commerceId,
  scheduledFrom: filter.scheduledFrom,
  scheduledTo: filter.scheduledTo,
  routeId: filter.routeId,
  routeName: filter.routeName,
  shippingNumber: filter.shippingNumber,
  status: filter.status,
});

export function useRoutesFilter() {
  const location = useLocation();
  const { getQueryParam, setQueryParams } = useQueryParams();

  const initialFilter: RoutesFilter = useMemo(
    () => ({
      driverIds: undefined,
      driverLabelsById: undefined,
      logisticOperatorId: undefined,
      commerceId: undefined,
      scheduledFrom: toScheduledFromIsoFromPickerDate(new Date()),
      scheduledTo: toScheduledToIsoFromPickerDate(new Date()),
      routeId: undefined,
      routeName: undefined,
      shippingNumber: undefined,
      status: undefined,
    }),
    []
  );

  const [filters, setFilters] = useState<RoutesFilter>(() =>
    resolveInitialRoutesFilter(initialFilter, location.search, getQueryParam)
  );

  useEffect(() => {
    saveRoutesFilterToLocalStorage(filters);
    setQueryParams(buildUrlQueryFromRoutesFilter(filters));
  }, [filters]);

  const mergeRoutesFilter = useCallback(
    (partialRoutesFilter: Partial<RoutesFilter>) => {
      setFilters((previous) => ({ ...previous, ...partialRoutesFilter }));
    },
    []
  );

  return {
    filters,
    setFilters,
    mergeRoutesFilter,
    initialFilter,
  };
}
